(() => {
  const sections = document.querySelectorAll("[data-baubau-special]");
  const wait = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));

  function waitForWindowLoad() {
    if (document.readyState === "complete") {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      window.addEventListener("load", resolve, { once: true });
    });
  }

  function setProgress(section, progress) {
    const number = section.querySelector("[data-baubau-progress]");
    const loading = section.querySelector("[data-baubau-loading]");

    if (number) {
      number.textContent = String(progress).padStart(2, "0");
    }

    if (loading) {
      loading.style.setProperty("--baubau-load-progress", String(progress / 100));
    }
  }

  async function runLoading(section) {
    const loading = section.querySelector("[data-baubau-loading]");
    const page = section.querySelector("[data-baubau-page]");

    if (!loading || !page || section.dataset.loadingEnabled !== "true") {
      document.body.classList.remove("baubau-is-loading");
      page?.classList.add("is-visible");
      setupScrollEffects(section);
      return;
    }

    document.body.classList.add("baubau-is-loading");
    const start = performance.now();
    const duration = 3400;
    const progressLoop = async () => {
      while (performance.now() - start < duration) {
        const elapsed = performance.now() - start;
        setProgress(section, Math.min(99, Math.round((elapsed / duration) * 100)));
        await new Promise(requestAnimationFrame);
      }
    };

    await Promise.all([
      progressLoop(),
      waitForWindowLoad(),
      document.fonts?.ready ?? Promise.resolve(),
      wait(duration),
    ]);

    setProgress(section, 100);
    loading.classList.add("is-finished");
    page.classList.add("is-visible");
    document.body.classList.remove("baubau-is-loading");
    setupScrollEffects(section);
  }

  function setupScrollEffects(section) {
    if (section.dataset.effectsInitialized === "true") {
      return;
    }

    section.dataset.effectsInitialized = "true";
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const mobileLayout = window.matchMedia("(max-width: 980px)");
    const revealTargets = section.querySelectorAll(".baubau-origin.baubau-reveal, .baubau-story.baubau-reveal");

    if (reducedMotion.matches) {
      revealTargets.forEach((target) => target.classList.add("is-revealed"));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-revealed");
          observer.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: "0px 0px -18% 0px",
      threshold: 0.18,
    });

    revealTargets.forEach((target) => {
      if (mobileLayout.matches && target.classList.contains("baubau-origin")) {
        target.classList.add("is-revealed");
        return;
      }

      observer.observe(target);
    });

    setupStoryParallax(section);
  }

  function setupStoryParallax(section) {
    const storyMedia = section.querySelector(".baubau-story__media");
    const storyImage = section.querySelector(".baubau-story__media img");

    if (!storyMedia || !storyImage) {
      return;
    }

    let ticking = false;

    const update = () => {
      ticking = false;
      const rect = storyMedia.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const progress = (viewportHeight - rect.top) / (viewportHeight + rect.height);
      const clampedProgress = Math.min(1, Math.max(0, progress));
      const offset = (0.5 - clampedProgress) * 34;
      storyImage.style.setProperty("--baubau-story-parallax-y", `${offset.toFixed(2)}px`);
    };

    const requestUpdate = () => {
      if (ticking) {
        return;
      }

      ticking = true;
      requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", requestUpdate, { passive: true });
    window.addEventListener("resize", requestUpdate);
  }

  sections.forEach((section) => {
    runLoading(section);
  });
})();
